import { memo, useCallback, useEffect } from "react";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "noval";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useTranslation from "next-translate/useTranslation";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Yup from "@utils/yup";
import SearchController from "@components/FormControllers/controllers/SearchController";

const searchSchema = Yup.object().shape({
  search: Yup.string().required(),
});

const Search = () => {
  const queryClient = useQueryClient();
  const router = useRouter();

  const { dispatch, reset } = useDispatch();
  const { lang } = useTranslation();
  const [homeCategory, search] = useSelector(["homeCategory", "search"]);

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(searchSchema),
    defaultValues: {
      search: "",
    },
  });

  const getFundsBySearch = useCallback(
    async ({ search }) => {
      reset(["search", "homeFundsSearch"]);

      dispatch({ search, resetHomeFunds: false });
      const newData = await dispatch("getFundsBySearch", {
        lang,
        fundsParams: homeCategory?.includes("all")
          ? {}
          : { fund_type: homeCategory },
      });
      return newData;
    },
    [lang, homeCategory]
  );

  const { data } = useQuery({
    queryKey: ["funds", search, homeCategory],
    queryFn: async () => await getFundsBySearch({ search }),
    enabled: !!search,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (data) {
      dispatch({ homeFundsSearch: data });
    }
  }, [data]);

  useEffect(() => {
    const handleRouteChange = (url) => {
      if (url !== "/home") {
        queryClient.removeQueries(["funds"]);
      }
    };

    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router, queryClient]);

  return (
    <form
      onSubmit={handleSubmit(({ search }) => dispatch({ search }))}
      className="w-full md:w-auto h-[43px] flex items-center"
    >
      <SearchController control={control} defaultValue={search} />
    </form>
  );
};

export default memo(Search);
